import { useState } from 'react';
import dynamic from 'next/dynamic';
import Link from 'next/link';

import ArrowRight from '@/icons/light/arrow-right.svg';
import PhoneOutgoing from '@/icons/light/phone-outgoing.svg';
import { Button, Card, IconText, Price, Tag } from '@/atoms';
import { ButtonSize, ButtonVariant } from '@/atoms/button/types';
import { IconTextVariant } from '@/atoms/icon-text/types';
import { TagVariant } from '@/atoms/tag/types';

import { useListingCardTheme } from '@/lib/contexts/listing-card-theme';
import { getPageName, gtmPush } from '@/utils';

import type { FunctionComponent } from 'react';
import type { TypeCardFlatProps } from './types';

const ModalForm = dynamic(() => import('@/molecules/modal-form'), {
  ssr: true,
});

const FormsTabs = dynamic(() => import('@/organisms/forms-tabs'), {
  ssr: true,
});

/**
 * CardFlat
 */
const CTA_ICONS = {
  arrow: ArrowRight,
  phone: PhoneOutgoing,
  none: null,
} as const;

export const CardFlat: FunctionComponent<TypeCardFlatProps> = ({
  tag,
  title,
  price,
  period,
  description,
  features,
  button,
  image,
  className = '',
  href,
  flat,
  oneCol = false,
}: TypeCardFlatProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const listingCardTheme = useListingCardTheme();
  const CtaIcon = CTA_ICONS[listingCardTheme.ctaIcon];
  const titleStyleClass = listingCardTheme.titleEstiloSlug
    ? `text-style-${listingCardTheme.titleEstiloSlug}`
    : 'text-body-l text-primary-500 font-medium';
  // Sin estilo CMS, `Price` aplica su `text-price` histórico sólo al importe;
  // ponerlo en el wrapper haría heredar el peso medium al sufijo "/mes".
  const priceStyleClass = listingCardTheme.priceEstiloSlug
    ? `text-style-${listingCardTheme.priceEstiloSlug}`
    : '';
  const isMarkhighlight = listingCardTheme.badgeStyle === 'markhighlight';
  // Cuando el tenant pica radius para el card y/o la imagen, el theme
  // expone una clase utility; cuando no, son `null` y el Card atom queda
  // con su `rounded-custom` / `rounded-t-custom` históricos.
  const cardRadiusCls = listingCardTheme.cardRadiusClass ?? '';
  const imageRadiusCls = listingCardTheme.imageRadiusClass ?? '';

  return (
    <>
      <ModalForm isOpen={isOpen} closeModal={() => setIsOpen(false)}>
        <FormsTabs flat={flat} noTitle />
      </ModalForm>

      <Card
        className={{
          // oneCol → horizontal Figma layout (LIST-card-Horizontal 2022:4376):
          //   imagen ocupa la mitad izquierda, contenido la derecha; altura
          //   fija ≈310px del Figma. Sin oneCol mantenemos la vertical de
          //   siempre (image-on-top + columna).
          card: oneCol
            ? `${cardRadiusCls} bg-bg-neutrals-0 flex h-[310px] flex-row items-stretch gap-5 p-(--listing-card-image-inset) ${className}`
            : `${cardRadiusCls} bg-bg-neutrals-0 flex h-full flex-col p-(--listing-card-image-inset) ${className}`,
          content: oneCol
            ? 'text-body flex h-full flex-1 flex-col p-4'
            : 'text-body flex h-full flex-col p-4',
          // En horizontal forzamos `rounded-custom` (4 esquinas) — el
          // default `rounded-t-custom` del Card atom sólo redondeaba arriba,
          // poco apto para una imagen tipo card-within-card al lado del
          // contenido.
          image: oneCol
            ? `${imageRadiusCls} rounded-custom! h-full flex-1`
            : `${imageRadiusCls} h-[200px] w-full shrink-0`,
        }}
        image={image}
        href={href}
        {...(tag && {
          label: (
            <Tag
              className={
                isMarkhighlight
                  ? 'absolute top-2 right-2 z-10'
                  : 'absolute top-4 right-4 z-10'
              }
              variant={
                isMarkhighlight ? TagVariant.MARKHIGHLIGHT : TagVariant.PROMO
              }
            >
              {listingCardTheme.badgeText || tag}
            </Tag>
          ),
        })}
      >
        <Link
          href={href}
          prefetch={false}
          className={`mb-1 line-clamp-2 grow [color:var(--listing-card-title-color,inherit)] ${titleStyleClass}`}
        >
          {title}
        </Link>

        <span
          className={`block [color:var(--listing-card-price-color,inherit)] ${priceStyleClass}`}
        >
          <Price
            price={price}
            period={period}
            unstyled={!!listingCardTheme.priceEstiloSlug}
          />
        </span>

        {description && description.length > 0 && (
          <p className="text-body-s mt-1">{description.join(' | ')}</p>
        )}

        <div
          className={
            oneCol
              ? 'mt-4 mb-6 flex flex-wrap gap-x-4 gap-y-2'
              : 'xs:grid-cols-[repeat(var(--listing-card-features-cols),minmax(0,1fr))] mt-4 mb-6 grid grid-cols-2 items-start gap-y-2'
          }
        >
          {features.slice(0, 6).map((feature, index: number) => (
            <IconText
              Icon={feature.Icon}
              variant={IconTextVariant.SMALL}
              key={index}
              value={feature.value}
              // El forzado `text-sm`+`truncate` que pide el grid de 3
              // columnas de HoM solo se aplica cuando el tenant ha picado
              // un titleEstiloSlug (proxy del rediseño de card); el render
              // legacy (2 cols) mantiene la tipografía histórica de IconText.
              className={
                oneCol
                  ? 'max-w-[165px] min-w-[150px] gap-x-2!'
                  : listingCardTheme.titleEstiloSlug
                    ? '[&_span]:truncate [&_span]:text-sm'
                    : undefined
              }
            />
          ))}
        </div>

        <Button
          size={ButtonSize.SMALL}
          variant={ButtonVariant.SECONDARY}
          trailingIcon={CtaIcon}
          onClick={() => {
            if (flat.ventaAlquiler === 'MED_EST')
              gtmPush({
                event: 'step_lead',
                step: '1-clic_reserva',
                form_name: 'Reservar',
                form_destination: document.URL,
                item_type: 'Media estancia',
                page_type: getPageName(),
                city_area: flat.nombreUE,
              });
            setIsOpen(true);
            button.onClick();
          }}
          className="rounded-card-cta! mt-auto max-w-max"
        >
          {button.label}
        </Button>
      </Card>
    </>
  );
};

CardFlat.displayName = 'CardFlat';
